<script setup lang="ts">
import { Button } from '@/components/lib/ui/button';
import { cn } from '@/lib/utils';
import { X } from '@lucide/vue';
import { useI18n } from 'vue-i18n';
import {
  DialogClose,
  DialogContent,
  type DialogContentEmits,
  type DialogContentProps,
  DialogOverlay,
  DialogPortal,
  useForwardPropsEmits,
} from 'reka-ui';
import { type HTMLAttributes, computed } from 'vue';

const { t } = useI18n();

const props = defineProps<
  DialogContentProps & {
    class?: HTMLAttributes['class'];
    customClose?: boolean;
  }
>();
const emits = defineEmits<DialogContentEmits>();

const delegatedProps = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { class: _, ...delegated } = props;

  return delegated;
});

const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
  <DialogPortal>
    <DialogOverlay
      :class="[
        'fixed inset-0 z-(--z-dialog) bg-black/80',
        'data-[state=open]:animate-in data-[state=open]:fade-in-0',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
      ]"
    />
    <DialogContent
      v-bind="forwarded"
      :class="
        cn(
          'bg-dialog fixed top-1/2 left-1/2 z-(--z-dialog) flex max-h-[90vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col border p-6 shadow-lg duration-200 sm:rounded-lg',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          props.class,
        )
      "
    >
      <slot />

      <template v-if="!customClose">
        <DialogClose class="absolute top-6 right-6" as-child>
          <Button variant="ghost" size="icon">
            <X class="size-4" />
            <span class="sr-only">{{ t('common.ui.close') }}</span>
          </Button>
        </DialogClose>
      </template>
    </DialogContent>
  </DialogPortal>
</template>
